import React, { Suspense } from 'react';
import { useRoutes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PrivateRoute from './PrivateRoute';
import Root from './Root';
import * as layoutConstants from '../constants/layout';

// All layouts/containers
import DefaultLayout from '../layouts/Default';
import DetachedLayout from '../layouts/Detached';
import AppShell from '../layouts/AppShell';
import TestMethods from '../pages/dashboard/Admin/Settings/TestMethods/Index';
import Units from '../pages/dashboard/Admin/Settings/Units/Index';
// import AnalysisCategory from '../pages/dashboard/Admin/Settings/AnalysisCategory';

// lazy load all the views

// auth
const Login = React.lazy(() => import('../pages/account/Login'));
const Logout = React.lazy(() => import('../pages/account/Logout'));
const Register = React.lazy(() => import('../pages/account/Register'));
const Confirm = React.lazy(() => import('../pages/account/Confirm'));
const ForgetPassword = React.lazy(() => import('../pages/account/ForgetPassword'));
const ResetPassword = React.lazy(() => import('../pages/account/ResetPassword'));
const LockScreen = React.lazy(() => import('../pages/account/LockScreen'));
const ChangePassword = React.lazy(() => import('../pages/account/ChangePassword'));
const Profile = React.lazy(() => import('../pages/account/Profile'));

// dashboard
const AdminDashboard = React.lazy(() => import('../pages/dashboard/Admin'));

const LabContact = React.lazy(() => import('../pages/dashboard/Admin/Settings/LabContact/LabContact'));
const LabDepartments = React.lazy(() => import('../pages/dashboard/Admin/Settings/LabDepartment/LabDepartments'));
const Sample = React.lazy(() => import('../pages/dashboard/Admin/Settings/Sample/Sample'));
const SampleLt = React.lazy(() => import('../pages/dashboard/Admin/Settings/Sample/SampleLt'));
const addSampleLt = React.lazy(() => import('../pages/dashboard/Admin/Settings/Sample/AddSampleLt'));
const Enquiry = React.lazy(() => import('../pages/dashboard/Admin/Settings/Enquiry'));
const AddEnquiry = React.lazy(() => import('../pages/dashboard/Admin/Settings/Enquiry/Add'));
const Order = React.lazy(() => import('../pages/dashboard/Admin/Settings/Order'));
const AddOrder = React.lazy(() => import('../pages/dashboard/Admin/Settings/Order/Add'));
const User = React.lazy(() => import('../pages/dashboard/Admin/Settings/User'));
const AddUser = React.lazy(() => import('../pages/dashboard/Admin/Settings/User/Add'));
const AnalysisCategory = React.lazy(() => import('../pages/dashboard/Admin/Settings/AnalysisCategory'));
const AnalysisService = React.lazy(() => import('../pages/dashboard/Admin/Settings/AnalysisService'));
const Clients = React.lazy(() => import('../pages/dashboard/Admin/Settings/Clients'));
const AddAnalysisCategory = React.lazy(() => import('../pages/dashboard/Admin/Settings/AnalysisCategory/Add'));
const AddAnalysisService = React.lazy(() => import('../pages/dashboard/Admin/Settings/AnalysisService/Add'));
const AddClients = React.lazy(() => import('../pages/dashboard/Admin/Settings/Clients/Add'));
const addSample = React.lazy(() => import('../pages/dashboard/Admin/Settings/Sample/Add'));
const bulkAddSample = React.lazy(() => import('../pages/dashboard/Admin/Settings/Sample/BulkAdd'));
const addFssaiSample = React.lazy(() => import('../pages/dashboard/Admin/Settings/Sample/AddFssai'));
const addLabContact = React.lazy(() => import('../pages/dashboard/Admin/Settings/LabContact/Add'));
const addLabDepartment = React.lazy(() => import('../pages/dashboard/Admin/Settings/LabDepartment/Add'));
const Invoice = React.lazy(() => import('../pages/dashboard/Admin/Settings/Invoice/Invoice'));
const Supplier = React.lazy(() => import('../pages/dashboard/Admin/Settings/Supplier'));
const AddSupplier = React.lazy(() => import('../pages/dashboard/Admin/Settings/Supplier/Add'));

const SupplierEvaluation = React.lazy(() => import('../pages/dashboard/Admin/Settings/SupplierEvaluation'));
const AddSupplierEvaluation = React.lazy(() => import('../pages/dashboard/Admin/Settings/SupplierEvaluation/Add'));

const SupplierRating = React.lazy(() => import('../pages/dashboard/Admin/Settings/SupplierRating'));
const AddSupplierRating = React.lazy(() => import('../pages/dashboard/Admin/Settings/SupplierRating/Add'));

const Inventory = React.lazy(() => import('../pages/dashboard/Admin/Settings/Inventory'));
const AddInventory = React.lazy(() => import('../pages/dashboard/Admin/Settings/Inventory/Add'));

const InventoryUsageLog = React.lazy(() => import('../pages/dashboard/Admin/Settings/InventoryUsageLog'));
const AddInventoryUsageLog = React.lazy(() => import('../pages/dashboard/Admin/Settings/InventoryUsageLog/Add'));

const Indent = React.lazy(() => import('../pages/dashboard/Admin/Settings/Indent'));
const AddIndent = React.lazy(() => import('../pages/dashboard/Admin/Settings/Indent/Add'));

const loading = () => <div className=""></div>;

const LoadComponent = ({ component: Component }) => (
    <Suspense fallback={loading()}>
        <Component />
    </Suspense>
);

const AllRoutes = () => {
    const { auth } = useSelector((state) => ({
        auth: state.Auth,
    }));

    let authCred = '';
    authCred = auth && auth.user && auth.user?.role;

    const getLayout = () => {
        return AppShell;
    };
    let Layout = getLayout();

    return useRoutes([
        { path: '/', element: <Root /> },
        {
            // public routes
            path: '/',
            element: <DefaultLayout />,
            children: [
                {
                    path: 'account',
                    children: [
                        { path: 'login', element: <LoadComponent component={Login} /> },
                        { path: 'register', element: <LoadComponent component={Register} /> },
                        { path: 'confirm', element: <LoadComponent component={Confirm} /> },
                        { path: 'forget-password', element: <LoadComponent component={ForgetPassword} /> },
                        { path: 'reset-password', element: <LoadComponent component={ResetPassword} /> },
                        { path: 'lock-screen', element: <LoadComponent component={LockScreen} /> },
                        { path: 'change-password', element: <LoadComponent component={ChangePassword} /> },
                        { path: 'logout', element: <LoadComponent component={Logout} /> },
                    ],
                },
            ],
        },
        {
            // auth protected routes
            path: '/',
            element: <PrivateRoute roles={auth && auth.user && auth.user?.role} component={Layout} />,
            children: [
                {
                    path: 'lims',
                    children: [
                        {
                            path: 'dashboard',
                            element:
                               ['admin', 'sales', 'lab manager', 'lab analyst','accounts', 'approval manager', 'purchase', 'lab assistant', 'quality assurance'].includes(authCred) ? (
                                     <LoadComponent component={AdminDashboard} />
                                ) : authCred === 'customer' ? (
                                    <LoadComponent component={AdminDashboard} />
                                ) : (
                                    null
                                ),
                        },
                        {
                            path: 'addSample',
                            element: <LoadComponent component={addSample} />,
                        },
                        {
                            path: 'bulkAddSample',
                            element: <LoadComponent component={bulkAddSample} />,
                        },
                        {
                            path: 'addFssaiSample',
                            element: <LoadComponent component={addFssaiSample} />,
                        },
                        {
                            path: 'Sample',
                            element: <LoadComponent component={Sample} />,
                        },
                        {
                            path: 'SampleLt',
                            element: <LoadComponent component={SampleLt} />,
                        },
                        {
                            path: 'addSampleLt',
                            element: <LoadComponent component={addSampleLt} />,
                        },
                        {
                            path: 'enquiry',
                            element: <LoadComponent component={Enquiry} />,
                        },
                        {
                            path: 'addEnquiry',
                            element: <LoadComponent component={AddEnquiry} />,
                        },
                        {
                            path: 'orders',
                            element: <LoadComponent component={Order} />,
                        },
                        {
                            path: 'addOrders',
                            element: <LoadComponent component={AddOrder} />,
                        },
                        {
                            path: 'clients',
                            element: <LoadComponent component={Clients} />,
                        },
                        {
                            path: 'addClients',
                            element: <LoadComponent component={AddClients} />,
                        },
                        {
                            path: 'invoice',
                            element: <LoadComponent component={Invoice} />,
                        },
                    ],
                },
                {
                    path: 'apps',
                    children: [
                        {
                            path: 'settings',
                            children: [
                                {
                                    path: 'labcontact',
                                    element: <LoadComponent component={LabContact} />,
                                },
                                {
                                    path: 'labdepartments',
                                    element: <LoadComponent component={LabDepartments} />,
                                },
                                {
                                    path: 'analysisCategory',
                                    element: <LoadComponent component={AnalysisCategory} />,
                                },
                                {
                                    path: 'addAnalysisCategory',
                                    element: <LoadComponent component={AddAnalysisCategory} />,
                                },
                                {
                                    path: 'analysisService',
                                    element: <LoadComponent component={AnalysisService} />,
                                },
                                {
                                    path: 'addAnalysisService',
                                    element: <LoadComponent component={AddAnalysisService} />,
                                },
                                {
                                    path: 'addLabDepartment',
                                    element: <LoadComponent component={addLabDepartment} />,
                                },
                                {
                                    path: 'addLabContact',
                                    element: <LoadComponent component={addLabContact} />,
                                },
                                {
                                    path: 'users',
                                    element: <LoadComponent component={User} />,
                                },
                                {
                                    path: 'addUsers',
                                    element: <LoadComponent component={AddUser} />,
                                },
                                {
                                    path: 'profile',
                                    element: <LoadComponent component={Profile} />,
                                },
                                {
                                    path: 'testmethods',
                                    element: <LoadComponent component={TestMethods} />,
                                },
                                {
                                    path: 'units',
                                    element: <LoadComponent component={Units} />,
                                },
                                {
                                    path: 'suppliers',
                                    element: <LoadComponent component={Supplier} />,
                                },
                                {
                                    path: 'addSuppliers',
                                    element: <LoadComponent component={AddSupplier} />,
                                },
                                {
                                    path: 'supplierEvaluation',
                                    element: <LoadComponent component={SupplierEvaluation} />,
                                },
                                {
                                    path: 'addSupplierEvaluation',
                                    element: <LoadComponent component={AddSupplierEvaluation} />,
                                },
                                {
                                    path: 'supplierRating',
                                    element: <LoadComponent component={SupplierRating} />,
                                },
                                {
                                    path: 'addSupplierRating',
                                    element: <LoadComponent component={AddSupplierRating} />,
                                },
                                {
                                    path: 'inventory',
                                    element: <LoadComponent component={Inventory} />,
                                },
                                {
                                    path: 'addInventory',
                                    element: <LoadComponent component={AddInventory} />,
                                },
                                {
                                    path: 'inventoryUsageLog',
                                    element: <LoadComponent component={InventoryUsageLog} />,
                                },
                                {
                                    path: 'addInventoryUsageLog',
                                    element: <LoadComponent component={AddInventoryUsageLog} />,
                                },
                                {
                                    path: 'indent',
                                    element: <LoadComponent component={Indent} />,
                                },
                                {
                                    path: 'addIndent',
                                    element: <LoadComponent component={AddIndent} />,
                                },
                            ],
                        },
                    ],
                },
            ],
        },
    ]);
};

export { AllRoutes };
